import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

// Month view of the published events. An event that runs for several days is marked on each day.
const EventCalendar = ({ events }) => {
  const today = new Date();
  const todayIso = iso(today.getFullYear(), today.getMonth(), today.getDate());

  // Start on the month of the next event, or of the latest one when all are in the past.
  const start = useMemo(() => {
    const dates = events.map((e) => e.date).filter(Boolean).sort();
    const next = dates.find((d) => d >= todayIso) || dates[dates.length - 1] || todayIso;
    return { year: Number(next.slice(0, 4)), month: Number(next.slice(5, 7)) - 1 };
  }, [events, todayIso]);
  const [view, setView] = useState(start);

  const first = new Date(view.year, view.month, 1);
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // Monday first
  const cells = [...Array(lead).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);

  const onDay = (day) => {
    const d = iso(view.year, view.month, day);
    return events.filter((e) => e.date && e.date <= d && d <= (e.endDate || e.date));
  };
  const inMonth = events
    .filter((e) => e.date && e.date.slice(0, 7) === iso(view.year, view.month, 1).slice(0, 7))
    .sort((a, b) => (a.date < b.date ? -1 : 1));

  const move = (delta) => {
    const d = new Date(view.year, view.month + delta, 1);
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };

  return (
    <div className="sc-calendar">
      <div className="sc-calendar__head">
        <button type="button" onClick={() => move(-1)} aria-label="Previous month">&lsaquo;</button>
        <h4>{MONTHS[view.month]} {view.year}</h4>
        <button type="button" onClick={() => move(1)} aria-label="Next month">&rsaquo;</button>
      </div>
      <div className="sc-calendar__grid" role="grid" aria-label={`${MONTHS[view.month]} ${view.year}`}>
        {DAYS.map((d) => <div key={d} className="sc-calendar__dow">{d}</div>)}
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} className="sc-calendar__cell is-empty"></div>;
          const list = onDay(day);
          const isToday = iso(view.year, view.month, day) === todayIso;
          return (
            <div key={day} className={`sc-calendar__cell${isToday ? ' is-today' : ''}${list.length ? ' has-event' : ''}`}>
              <span className="sc-calendar__num">{day}</span>
              {list.slice(0, 2).map((e) => (
                <Link key={e.id} to={`/events/${e.id}`} title={e.title}>{e.title}</Link>
              ))}
              {list.length > 2 && <small>+{list.length - 2} more</small>}
            </div>
          );
        })}
      </div>
      <div className="sc-calendar__list">
        <h5>Events in {MONTHS[view.month]} {view.year}</h5>
        {inMonth.length === 0 ? (
          <p>No events this month.</p>
        ) : (
          <ul>
            {inMonth.map((e) => (
              <li key={e.id}>
                <span>{e.day} {e.month.slice(0, 3)}</span>
                <Link to={`/events/${e.id}`}>{e.title}</Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EventCalendar;
